import React, { useContext, useEffect, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Button, IconButton } from '@one-thd/sui-atomic-components';
import { Mic, Pause } from '@one-thd/sui-icons';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

/**
 * TapToSpeak component for speech-to-text functionality
 */
export const TapToSpeak = ({
  onTranscriptChange,
  onListeningChange,
  disabled,
  defaultText
}) => {
  // Speech recognition hook
  const {
    transcript,
    listening,
    browserSupportsSpeechRecognition
  } = useSpeechRecognition();

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  // Track previous transcript to avoid infinite loops
  const prevTranscriptRef = useRef('');

  useEffect(() => {
    if (listening && disabled) {
      SpeechRecognition.stopListening();
    }
  }, [disabled, listening]);

  // Notify parent of listening state changes
  useEffect(() => {
    if (onListeningChange) {
      onListeningChange(listening);
    }
  }, [listening, onListeningChange]);

  // Update parent component when transcript changes
  useEffect(() => {
    // Only call onTranscriptChange if transcript has actually changed and is not empty
    if (transcript && transcript !== prevTranscriptRef.current && onTranscriptChange) {
      prevTranscriptRef.current = transcript;
      onTranscriptChange(transcript);
    }
  }, [transcript, onTranscriptChange]);

  const handleSpeechInput = () => {
    if (!browserSupportsSpeechRecognition) {
      // Browser doesn't support speech recognition, do nothing
      return;
    }

    if (listening) {
      SpeechRecognition.stopListening();
    } else {
      SpeechRecognition.startListening({
        continuous: true,
        language: 'en-US'
      });
    }
  };

  const buttonText = listening ? 'Tap to pause' : defaultText;

  const buttonWrapper = classNames('sui-bg-primary', {
    'sui-w-64': !isMobile,
    'sui-border-2 sui-border-solid sui-rounded-lg': isMobile,
    'sui-border-primary': disabled && isMobile,
    'sui-border-accent': !disabled && isMobile,
    'sui-w-fit': isMobile,
  });

  if (!browserSupportsSpeechRecognition) {
    return null;
  }

  return (
    <div className={buttonWrapper}>
      {
        isMobile ? (
          <IconButton
            icon={listening ? Pause : Mic}
            onClick={handleSpeechInput}
            disabled={disabled}
            edge
            color="brand"
            buttonSize="normal"
            iconSize="regular"
            style={{ width: '4rem', height: '4rem' }}
          />
        ) : (
          <Button
            variant="secondary"
            endIcon={listening ? Pause : Mic}
            onClick={handleSpeechInput}
            disabled={disabled}
            fullWidth={!isMobile}
          >
            {
              !isMobile ? buttonText : (
                <span className="sui-border-primary sui-border-1" />
              )
            }
          </Button>
        )
      }
    </div>
  );
};

TapToSpeak.propTypes = {
  onTranscriptChange: PropTypes.func.isRequired,
  onListeningChange: PropTypes.func,
  disabled: PropTypes.bool,
  defaultText: PropTypes.string
};

TapToSpeak.defaultProps = {
  disabled: false,
  defaultText: 'Tap to speak',
  onListeningChange: null
};

export default TapToSpeak;
